import fullDate from '@/filters/fullDate';
import { trans } from '@/mixins/i18n';
import { upcomingOccurrences } from '@/Utils/recurrenceOccurrences';
import { sortedWeekdayLabels } from '@/Utils/recurrenceToHumanReadableText';
import { addWindow, decomposeWindow, windowDurationLabel } from '@/Utils/scheduleWindow';
import { DateTime } from 'luxon';
import { computed, ComputedRef } from 'vue';

export type SummaryLine = { id: string; text: string; tone: 'info' | 'warning'; bullet?: boolean };

type Recurrence = {
    repeat: string;
    interval: number | string;
    weekdays: string[];
    stopCondition: string;
    count: number | string;
    until: DateTime | string | null;
};

const CADENCE_UNITS: Record<string, string> = {
    hourly: 'hours',
    daily: 'days',
    weekly: 'weeks',
    monthly: 'months',
    yearly: 'years',
};

export function useScheduleSummary(src: {
    recurrence: () => Recurrence;
    startDate: () => DateTime | null | undefined;
    endDate: () => DateTime | null | undefined;
    isPaused: () => boolean;
    slaEndDate: () => string | null;
    locale: () => string;
    isWorkOrder?: () => boolean;
}): { lines: ComputedRef<SummaryLine[]> } {
    const lines = computed<SummaryLine[]>(() => {
        const out: SummaryLine[] = [];
        const recurrence = src.recurrence();
        const start = src.startDate();
        const end = src.endDate();
        const locale = src.locale() || 'en';
        const once = !recurrence || recurrence.repeat === 'once';
        const now = DateTime.now();

        // 24-hour time to match the form's time inputs (TimePicker uses HH:mm).
        const atLabel = (moment: DateTime) => `${fullDate(moment, locale)} ${trans('general.time.at')} ${moment.toFormat('HH:mm')}`;
        const key = (suffix: string) => `quality_assurance.configuration.modal.summary.${suffix}`;

        // PM (work-order) schedules get an action-oriented narrative: when the
        // next maintenance comes up, what it triggers, when it goes overdue, and
        // when the following one is scheduled.
        if (src.isWorkOrder?.() && start?.isValid) {
            const occurrences = upcomingOccurrences(start, recurrence, now, 2);
            const first = occurrences[0];
            const window = decomposeWindow(start, end);

            if (first) {
                out.push({ id: 'pm-intro', tone: 'info', text: trans(key('pm.intro'), { date: fullDate(first, locale) }) });
                out.push({ id: 'pm-notify', tone: 'info', bullet: true, text: trans(key('pm.notify')) });
                out.push({ id: 'pm-work-order', tone: 'info', bullet: true, text: trans(key('pm.work_order')) });

                if (window) {
                    out.push({
                        id: 'pm-window',
                        tone: 'info',
                        bullet: true,
                        text: trans(key('pm.window'), { duration: windowDurationLabel(window) }),
                    });
                    out.push({
                        id: 'pm-overdue',
                        tone: 'info',
                        text: trans(key('pm.overdue'), { date: fullDate(addWindow(first, window), locale) }),
                    });
                }

                if (!once && occurrences[1]) {
                    out.push({ id: 'pm-next', tone: 'info', text: trans(key('pm.next'), { date: fullDate(occurrences[1], locale) }) });
                }

                if (!once && src.isPaused()) {
                    out.push({ id: 'paused', tone: 'warning', text: trans(key('paused')) });
                }

                return out;
            }
            // No upcoming occurrence (e.g. series ended) — fall through to the
            // generic summary below.
        }

        if (start?.isValid) {
            if (once) {
                out.push({
                    id: 'cadence',
                    tone: 'info',
                    text: trans('quality_assurance.configuration.modal.summary.once', { start: atLabel(start) }),
                });
            } else {
                const interval = Number(recurrence.interval) || 1;
                const unitLabel = trans(`general.unit.${CADENCE_UNITS[recurrence.repeat] ?? 'days'}`, { '%d': interval });
                const weekdays =
                    recurrence.repeat === 'weekly' && recurrence.weekdays?.length > 0
                        ? ` ${trans('general.component.repeat.on')} ${sortedWeekdayLabels([...recurrence.weekdays], locale).join(', ')}`
                        : '';

                out.push({
                    id: 'cadence',
                    tone: 'info',
                    text: trans('quality_assurance.configuration.modal.summary.recurring', {
                        cadence: interval > 1 ? `${interval} ${unitLabel}` : unitLabel,
                        weekdays,
                        start: atLabel(start),
                    }),
                });
            }
        }

        // Spell out the next concrete occurrence dates for weekly schedules so
        // the cycle-anchoring shift (selected weekdays resolved from the start
        // date, not the calendar week) is visible rather than implied.
        if (!once && recurrence.repeat === 'weekly' && recurrence.weekdays?.length && start?.isValid) {
            // Show one full cycle so the selected weekdays read as a complete
            // set (e.g. "Mon, Tue") instead of cutting off mid-cycle; a single
            // selected day shows two occurrences so the weekly repeat is clear.
            const target = recurrence.weekdays.length === 1 ? 2 : recurrence.weekdays.length;
            const previewLimit = recurrence.stopCondition === 'count' ? Math.min(target, Number(recurrence.count) || 1) : target;
            const occurrences = upcomingOccurrences(start, recurrence, now, previewLimit);

            if (occurrences.length) {
                // Locale-adaptive date order (weekday/month/day ordering follows
                // the locale). Non-breaking spaces keep each date intact and the
                // ellipsis attached, so the list only ever wraps between dates.
                const dates = occurrences
                    .map((occurrence) =>
                        occurrence.setLocale(locale).toLocaleString({ weekday: 'short', month: 'short', day: 'numeric' }).replace(/ /g, '\u00A0'),
                    )
                    .join(' \u00B7 ');
                const ellipsis = recurrence.stopCondition === 'count' && occurrences.length >= (Number(recurrence.count) || 1) ? '' : '\u00A0…';

                out.push({
                    id: 'occurrences',
                    tone: 'info',
                    text: trans('quality_assurance.configuration.modal.summary.next_tasks', { dates: `${dates}${ellipsis}` }),
                });
            }
        }

        const window = decomposeWindow(start, end);
        if (window) {
            out.push({
                id: 'window',
                tone: 'info',
                text: once
                    ? trans('quality_assurance.configuration.modal.summary.once_window', {
                          duration: windowDurationLabel(window),
                          end: atLabel(end as DateTime),
                      })
                    : trans('quality_assurance.configuration.modal.summary.window', { duration: windowDurationLabel(window) }),
            });
        }

        const slaEnd = src.slaEndDate();
        if (slaEnd) {
            out.push({
                id: 'ends',
                tone: 'info',
                text: trans('quality_assurance.configuration.modal.sla.window_hint', { end: fullDate(slaEnd, locale) }),
            });
        } else if (!once) {
            if (recurrence.stopCondition === 'count') {
                const count = Number(recurrence.count) || 1;
                out.push({
                    id: 'ends',
                    tone: 'info',
                    text: trans('quality_assurance.configuration.modal.summary.ends_count', { '%d': count, count }),
                });
            } else if (recurrence.stopCondition === 'until' && recurrence.until) {
                out.push({
                    id: 'ends',
                    tone: 'info',
                    text: trans('quality_assurance.configuration.modal.summary.ends_until', { date: fullDate(recurrence.until, locale) }),
                });
            } else {
                out.push({
                    id: 'ends',
                    tone: 'info',
                    text: trans('quality_assurance.configuration.modal.summary.ends_never'),
                });
            }
        }

        if (!once && start?.isValid && start < now) {
            const [next] = upcomingOccurrences(start, recurrence, now, 1);
            out.push({
                id: 'anchor',
                tone: 'info',
                text: next
                    ? trans('quality_assurance.configuration.modal.summary.past_start', {
                          date: fullDate(start, locale),
                          next: fullDate(next, locale),
                      })
                    : trans('quality_assurance.configuration.modal.summary.past_start_anchor_only', { date: fullDate(start, locale) }),
            });
        }

        if (once && end?.isValid && end < now) {
            out.push({
                id: 'past_due',
                tone: 'warning',
                text: trans('quality_assurance.configuration.modal.summary.past_due_warning'),
            });
        }

        if (!once && src.isPaused()) {
            out.push({
                id: 'paused',
                tone: 'warning',
                text: trans('quality_assurance.configuration.modal.summary.paused'),
            });
        }

        return out;
    });

    return { lines };
}
